import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Link, useLocation } from 'react-router-dom';
import { LucideIcon, X } from 'lucide-react';
import { SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';

interface FavoriteMenuItemProps {
  id: string;
  url: string;
  title: string;
  icon: LucideIcon;
  color: string;
  onRemove: () => void;
}

export function FavoriteMenuItem({ id, url, title, icon: Icon, color, onRemove }: FavoriteMenuItemProps) {
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <SidebarMenuItem 
      ref={setNodeRef} 
      style={style}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <SidebarMenuButton
        asChild
        className={cn(
          'group relative rounded-lg transition-all duration-200',
          isActive(url)
            ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
            : 'hover:bg-sidebar-accent/50',
          isDragging && 'opacity-50'
        )}
      >
        <div className="flex items-center gap-3 px-3 py-2 relative">
          <Link to={url} className="flex items-center gap-3 flex-1">
            <div
              {...listeners}
              {...attributes}
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-md text-white transition-transform group-hover:scale-110 cursor-grab active:cursor-grabbing',
                color
              )}
            >
              <Icon className="h-4 w-4" />
            </div>
            <span className="text-sm">{title}</span>
          </Link>
          {isHovered && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRemove();
              }}
              className="absolute right-2 p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
            >
              <X className="h-3 w-3" />
            </button>
          )}
          {isActive(url) && (
            <div className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 bg-primary rounded-r-full" />
          )}
        </div>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}
